import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import {
  type PalantirKeyValueEntry,
  type PalantirLabelColors,
  PalantirKeyValueListComponent,
  PalantirTagComponent,
} from '@flux/design-system';
import type { ChatComponent } from '../../../models/chat.model';
import {
  asFluxDetailData,
  asFluxDetailPresentation,
  type FluxDetailStatusTheme,
} from '../../../models/flux-detail.model';

/** Cores de `palantir-tag` para o status — subconjunto de `PalantirLabelColors`. */
type FluxDetailTagColor = Extract<
  PalantirLabelColors,
  'blue' | 'grey' | 'green' | 'pink' | 'orange' | 'red'
>;

const STATUS_COLORS: Record<FluxDetailStatusTheme, FluxDetailTagColor> = {
  blue: 'blue',
  default: 'grey',
  green: 'green',
  magenta: 'pink',
  orange: 'orange',
  red: 'red',
};

/**
 * Renderer do componente `flux.detail@1` emitido pela Iara: detalhe de uma única
 * entidade (card, fluxo, etapa…) com título, status (tag), narrativa e campos
 * chave/valor. Faz o narrowing seguro do envelope; payload inválido cai no
 * `fallbackText`. As ações (abrir/arquivar…) são renderizadas pelo host.
 */
@Component({
  selector: 'app-flux-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PalantirTagComponent, PalantirKeyValueListComponent],
  templateUrl: './flux-detail.component.html',
  styleUrl: './flux-detail.component.scss',
})
export class FluxDetailComponent {
  public readonly component = input.required<ChatComponent>();

  protected readonly data = computed(() => asFluxDetailData(this.component().data));
  protected readonly presentation = computed(() =>
    asFluxDetailPresentation(this.component().presentation),
  );

  protected readonly statusColor = computed<FluxDetailTagColor>(
    () => STATUS_COLORS[this.presentation()?.statusTheme ?? 'default'],
  );

  /** Campos mapeados para o `palantir-key-value-list` (valores já formatados). */
  protected readonly entries = computed<PalantirKeyValueEntry[]>(
    () =>
      this.data()?.fields.map((field) => ({
        label: field.label,
        value: field.value,
        mono: field.valueKind === 'link',
      })) ?? [],
  );
}
